import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {TextBoxComponent} from '@progress/kendo-angular-inputs';
import {Observable, of} from 'rxjs';

@Component({
  selector: 'gc-tree-dropdown',
  templateUrl: './tree-dropdown.component.html',
  styleUrls: ['./tree-dropdown.component.scss']
})
export class TreeDropdownComponent implements OnInit {
  constructor() {
  }

  @Input()
  get value(): number {
    return this.selectedKeys[0];
  }

  set value(v) {
    this.selectedKeys = [v];
    this.findText();
  }

  @Input()
  get items(): any[] {
    return this.data;
  }

  set items(v) {
    this.data = v;
    this.parsedData = this.data;
    this.findText();
  }

  selectedKeys: number[] = [];
  expandedKeys: any = [];
  popupWidth = null;
  data: any[] = [];
  show = false;
  text: string = null;
  @Output() valueChange = new EventEmitter<number>();
  @Input() width = null;
  @Output() selected = new EventEmitter<any>();

  public parsedData: any[] = this.data;
  public searchTerm = '';

  ngOnInit(): void {
    this.findText();
  }

  public onToggle(textBox: TextBoxComponent): void {
    this.show = !this.show;

    if (!this.show) {
      textBox.value = this.text;
    }
  }

  public selectionChange({dataItem}: any): void {
    this.text = dataItem.text;
    this.valueChange.emit(dataItem.value);
    this.selected.emit(dataItem);
    this.show = false;
  }

  private findText(data = null, indexes = []): boolean {
    if (!data) {
      data = this.data;
      this.expandedKeys = [];
      this.text = null;
      if (!data || data.length < 1 || !this.value) {
        return false;
      }
    }

    // Found
    let index = data.findIndex(item => item.value === this.value);
    if (index > -1) {
      this.text = data[index].text;
      this.expandedKeys = indexes;
      return true;
    }

    // Find in child;
    index = 0;
    for (const item of data) {
      if (item.items) {
        const childIndexes = [...indexes];
        if (indexes.length > 0) {
          childIndexes.push(indexes[indexes.length - 1] + '_' + index.toString());
        } else {
          childIndexes.push(index.toString());
        }
        if (this.findText(item.items, childIndexes)) {
          break;
        }
      }
      index++;
    }
    return false;
  }

  public onkeyup(textBox: TextBoxComponent, event: KeyboardEvent): void {
    const value = textBox.value;
    if ( event.key === 'Escape') {
      this.show = false;
      textBox.value = this.text;
      return;
    }
    this.parsedData = this.search(this.data, value);
    this.expandAll();
  }

  public onfocus(value: string): void{
    this.parsedData = this.search(this.data, value);
    this.expandTo(value);
    this.show = true;
  }

  public onblur(textBox: TextBoxComponent): void {
    if (this.parsedData.length === 0) {
      textBox.value = this.text;
      this.show = false;
    }
  }

  public expandTo(term: string, data: any[] = null, indexes: any[]= []): boolean {
    if (!data) {
      data = this.parsedData;
      this.expandedKeys = [];
      if (!data || data.length < 1 || !term) {
        return false;
      }
    }

    // Found
    let index = data.findIndex(item => this.contains(item.text, term));
    if (index > -1) {
      this.expandedKeys = indexes;
      return true;
    }

    // Find in child;
    index = 0;
    for (const item of data) {
      if (item.items) {
        const childIndexes = [...indexes];
        if (indexes.length > 0) {
          childIndexes.push(indexes[indexes.length - 1] + '_' + index.toString());
        } else {
          childIndexes.push(index.toString());
        }
        if (this.expandTo(term, item.items, childIndexes)) {
          break;
        }
      }
      index++;
    }
    return false;
  }

  public expandAll(data: any[] = null, indexes: any[]= [], parent: string = null): void {
    let init = false;
    if (!data) {
      init = true;
      data = this.parsedData;
      this.expandedKeys = [];
      if (!data || data.length < 1) {
        return;
      }
    }

    // Find in child;
    let index = 0;
    for (const item of data) {
      if (item.items) {
        let node = index.toString();
        if (parent) {
          node = parent + '_' + index.toString();
        }
        indexes.push(node);
        this.expandAll(item.items, indexes, node);
      }
      index++;
    }
    if (init) {
      this.expandedKeys = indexes;
    }
  }

  public search(items: any[], term: string): any[] {
    if (!term || term.trim() === '') {
      return items;
    }
    return items.reduce((acc, item) => {
      if (this.contains(item.text, term)) {
        acc.push(item);
      } else if (item.items && item.items.length > 0) {
        const newItems = this.search(item.items, term);

        if (newItems.length > 0) {
          acc.push({ text: item.text, value: item.value, items: newItems });
        }
      }

      return acc;
    }, []);
  }

  public contains(text: string, term: string): boolean {
    if (!text || !term) {
      return false;
    }
    return text.toLowerCase().indexOf(term.toLowerCase()) >= 0;
  }

  /**
   * A function that returns an observable instance which contains the
   * [child nodes](https://www.telerik.com/kendo-angular-ui/components/treeview/api/TreeViewComponent/#toc-children)
   * for a given parent node.
   */
  public children = (dataitem: any): Observable<any[]> => of(dataitem.items);

  /**
   * A function that determines whether a given node
   * [has children](https://www.telerik.com/kendo-angular-ui/components/treeview/api/TreeViewComponent/#toc-haschildren).
   */
  public hasChildren = (dataitem: any): boolean => !!dataitem.items;
}
